import {
  ImportFieldDefinition,
  ColumnMapping,
  ImportRowValidation,
} from "../DataImport.types";

/**
 * Validates resolved row data against field definitions.
 *
 * Checks:
 * 1. Required fields are present and non-empty
 * 2. Type constraints (number is numeric, select value is in options, etc.)
 * 3. Custom field.validate() functions
 * 4. Custom field.warn() functions (non-blocking warnings)
 */
export function validateRows(
  resolvedRows: Record<string, unknown>[],
  fields: ImportFieldDefinition[],
  columnMapping: ColumnMapping,
  csvRows?: Record<string, string>[]
): ImportRowValidation[] {
  return resolvedRows.map((row, rowIndex) => {
    const errors: Record<string, string> = {};
    const warnings: Record<string, string> = {};

    for (const field of fields) {
      // Skip fields that are not applicable to this row entirely:
      // no required check, no resolver-failure warning, no type/custom validate.
      if (field.isApplicable && !field.isApplicable(row)) {
        continue;
      }

      const value = row[field.key];
      const isMapped = !!columnMapping[field.key];
      const isEmptyArray = Array.isArray(value) && value.length === 0;
      const isEmptyValue =
        value === null || value === undefined || value === "" || isEmptyArray;

      // Required field check
      if (field.required && isEmptyValue) {
        if (!isMapped) {
          errors[
            field.key
          ] = `${field.label} is required but no column was mapped`;
        } else {
          errors[field.key] = `${field.label} is required`;
        }
      }

      // Resolver failure detection: if field had a resolver but value is null
      // or an empty array, and the original CSV column was mapped and had data
      if (
        field.resolver &&
        (value === null || (Array.isArray(value) && value.length === 0)) &&
        isMapped
      ) {
        const csvColumn = columnMapping[field.key];
        const rawValue =
          csvRows && csvColumn ? csvRows[rowIndex]?.[csvColumn] : undefined;
        if (rawValue !== undefined && rawValue.trim() !== "") {
          // If the field has a custom validate, let it produce an error
          // instead of the generic resolver failure warning
          if (field.validate) {
            const customError = field.validate(value, row, rawValue);
            if (customError) {
              errors[field.key] = customError;
              continue;
            }
          }
          warnings[field.key] = field.unresolvedWarning
            ? field.unresolvedWarning(rawValue, row)
            : `Could not resolve value "${rawValue}" for ${field.label}`;
        }
      }

      // Skip further validation if value is empty and not required,
      // but still run custom validate/warn so fields can produce their own
      // "required" errors or resolver-drop warnings when needed.
      if (isEmptyValue) {
        if (isMapped) {
          const csvColumn = columnMapping[field.key];
          const rawValue =
            csvRows && csvColumn ? csvRows[rowIndex]?.[csvColumn] : undefined;
          if (field.validate) {
            const customError = field.validate(value, row, rawValue);
            if (customError) {
              errors[field.key] = customError;
              continue;
            }
          }
          if (field.warn) {
            const warning = field.warn(value, row, rawValue);
            if (warning) {
              warnings[field.key] = warning;
            }
          }
        }
        continue;
      }

      // Type-specific validation
      const typeError = validateType(value, field);
      if (typeError) {
        errors[field.key] = typeError;
        continue;
      }

      // Custom validation
      if (field.validate) {
        const csvColumn = columnMapping[field.key];
        const rawValue =
          csvRows && csvColumn ? csvRows[rowIndex]?.[csvColumn] : undefined;
        const customError = field.validate(value, row, rawValue);
        if (customError) {
          errors[field.key] = customError;
          continue;
        }
      }

      // Custom warning check (non-blocking)
      if (field.warn && isMapped) {
        const csvColumn = columnMapping[field.key];
        const rawValue =
          csvRows && csvColumn ? csvRows[rowIndex]?.[csvColumn] : undefined;
        const warning = field.warn(value, row, rawValue);
        if (warning) {
          warnings[field.key] = warning;
        }
      }
    }

    return {
      rowIndex,
      data: row,
      errors,
      warnings,
      isValid: Object.keys(errors).length === 0,
    };
  });
}

function validateType(
  value: unknown,
  field: ImportFieldDefinition
): string | null {
  switch (field.type) {
    case "number": {
      if (typeof value === "string") {
        return `"${value}" is not a valid number`;
      }
      if (typeof value === "number" && isNaN(value)) {
        return `Invalid number value`;
      }
      return null;
    }

    case "boolean": {
      if (typeof value !== "boolean") {
        return `"${String(
          value
        )}" is not a valid boolean (expected true/false, yes/no, 1/0)`;
      }
      return null;
    }

    case "select": {
      if (field.options) {
        const matchesOption = (candidate: unknown) =>
          field.options!.some(
            (opt) => opt.value === candidate || opt.label === candidate
          );

        if (Array.isArray(value)) {
          const invalidValues = value.filter((entry) => !matchesOption(entry));
          if (invalidValues.length > 0) {
            return `"${invalidValues.join(", ")}" is not a valid option for ${
              field.label
            }`;
          }
          return null;
        }

        if (!matchesOption(value)) {
          return `"${String(value)}" is not a valid option for ${field.label}`;
        }
      }
      return null;
    }

    case "date": {
      if (typeof value === "string") {
        const parsed = Date.parse(value);
        if (isNaN(parsed)) {
          return `"${value}" is not a valid date`;
        }
      }
      return null;
    }

    case "text":
    default:
      return null;
  }
}
